import React, { useState, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import debounce from 'lodash/debounce';
import styles from './Search.module.scss';
import { ReactComponent as IconNotification } from 'images/new-icons/notification-dark.svg';
import { ReactComponent as IconSearch } from 'images/new-icons/search-dark.svg';
import { ReactComponent as IconSearchBlack } from 'images/new-icons/search-black.svg';
import { ReactComponent as IconBack } from 'images/new-icons/arrow-go-back.svg';
import { ReactComponent as Clear } from 'images/new-icons/exit.svg';
import { ReactComponent as Filter } from 'images/new-icons/filter.svg';
import { ReactComponent as ManicureIcon } from 'images/new-icons/manicure-search-icon.svg';
import { useNavigate } from 'react-router-dom';
import Button from 'components/shared/Button';
import useIsAuth from 'sdk/hooks/useIsAuth';
import supabase from 'sdk/api';
import useGetSearchHistory from 'sdk/hooks/salon/useGetSearchHistory';
import useGetPopularSeachSuggestions from 'sdk/hooks/salon/useGetPopularSeachSuggestions';
import { useActions } from 'store/useActions';
import { set } from 'lodash';

interface SearchProps {
  searchText: string;
  setSearchText: (text: string) => void;
  handleFilterButton: () => void;
  isSearchActive: boolean;
  setIsSearchActive: (active: boolean) => void;
  onSearch: () => void;
  setIsExpanded: (expanded: boolean) => void;
  setShowSearchHistory: (show: boolean) => void;
  showSearchHistory: boolean;
}

const Search = ({
  searchText,
  setSearchText,
  handleFilterButton,
  isSearchActive,
  setIsSearchActive,
  onSearch,
  setIsExpanded,
  setShowSearchHistory,
  showSearchHistory,
}: SearchProps) => {
  const navigate = useNavigate();
  const { setIsButtonMenuVisible } = useActions();

  const { data: isAuthData } = useIsAuth();
  const { data: searchHistory, refetch: refetchSearchHistory } = useGetSearchHistory(
    isAuthData?.user?.id,
  );
  const { data: popularSearchSuggestions, refetch: refetchPopularSearchSuggestions } =
    useGetPopularSeachSuggestions();

  const handleChange = e => {
    const value = e.target.value;
    setSearchText(value);
  };

  const handleClear = () => {
    setSearchText('');
  };

  const handleSearch = async () => {
    const searchTextIsExists = searchHistory?.some(item => item.text === searchText);
    onSearch();
    setShowSearchHistory(false);

    if (isAuthData?.isAuth && !searchTextIsExists) {
      const { error: errorAddingSearchHistory } = await supabase
        .from('salons_search_history')
        .insert({
          user_id: isAuthData?.user?.id,
          text: searchText,
        });

      if (errorAddingSearchHistory) {
        console.log('Error while adding search history: ', errorAddingSearchHistory);
      }
    }
  };

  const filteredSearchHistory = searchHistory?.filter(item =>
    item.text.toLowerCase().includes(searchText.toLowerCase()),
  );

  const handleHistoryItemClick = (text: string) => {
    setSearchText(text);

    onSearch();
    setShowSearchHistory(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div className={clsx(styles.container)}>
        {isSearchActive ? (
          <IconBack
            onClick={() => {
              setIsButtonMenuVisible(true);
              setIsSearchActive(false);
            }}
            className={styles.icon}
          />
        ) : (
          <IconNotification
            className={styles.icon}
            onClick={() => navigate('/user-notifications')}
          />
        )}

        {!isSearchActive && <div className={styles.title}>Salons</div>}

        {isSearchActive ? (
          <div className={styles.search}>
            <IconSearchBlack />
            <input
              type="text"
              value={searchText}
              onChange={handleChange}
              onFocus={() => {
                refetchSearchHistory();
                setShowSearchHistory(true);
              }}
              className={styles.searchInput}
              placeholder="Type anything"
            />
            <div className={styles.icon}>
              {searchText ? (
                <Clear onClick={handleClear} />
              ) : (
                <Filter onClick={handleFilterButton} />
              )}
            </div>
          </div>
        ) : (
          <IconSearch
            onClick={() => {
              setIsSearchActive(true);
              setIsExpanded(false);
              refetchSearchHistory();
            }}
            className={styles.icon}
          />
        )}
        {isSearchActive && (
          <Button style={{ margin: '0 10px' }} onClick={handleSearch}>
            Search
          </Button>
        )}
      </div>
      {isSearchActive && showSearchHistory && (
        <div className={styles.search_history}>
          {filteredSearchHistory?.map(item => (
            <div
              key={item.id}
              className={styles.search_history_item}
              onClick={() => handleHistoryItemClick(item.text)}
            >
              <div className={styles.search_history_item_content}>
                <ManicureIcon />
                <div className={styles.search_history_item_content_text}>{item.text}</div>
              </div>
              <Clear
                onClick={e => {
                  e.stopPropagation();
                  const deleteHistory = async () => {
                    const { error: errorDeletingSearchHistory } = await supabase
                      .from('salons_search_history')
                      .delete()
                      .eq('id', item.id);

                    if (errorDeletingSearchHistory) {
                      console.log(
                        'Error while deleting search history: ',
                        errorDeletingSearchHistory,
                      );
                    }

                    refetchSearchHistory();
                  };
                  deleteHistory();
                }}
              />
            </div>
          ))}
          <div className={styles.search_history_popular}>Popular suggestions</div>
          {popularSearchSuggestions?.map(item => (
            <div
              key={item.id}
              className={styles.search_history_item}
              onClick={() => handleHistoryItemClick(item.text)}
            >
              <div className={styles.search_history_item_content}>
                <ManicureIcon />
                <div className={styles.search_history_item_content_text}>{item.text}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Search;
